/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	WorkItem
} from '@shared/business-logic-entities/work-item';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	EventHelper
} from '@shared/helpers/event.helper';
import {
	Activity
} from '@shared/implementations/application-data/activity';
import {
	ResolverService
} from '@shared/services/resolver.service';

/**
 * A Business Logic Entity of Action Required Work Item.
 *
 * @class ActionRequiredWorkItem
 * @export
 */
export class ActionRequiredWorkItem
	extends WorkItem
{
	/**
	 * Initializes a new instance of the action required work item
	 * business logic entity.
	 *
	 * @param {IWorkItem} workItem
	 * The underlying work item.
	 * @param {ResolverService} resolverService
	 * The resolver service.
	 * @memberof ActionRequiredWorkItem
	 */
	public constructor(
		workItem: WorkItem,
		public resolverService: ResolverService)
	{
		super(
			workItem,
			resolverService);
	}

	/**
	 * Marks the work item done/approved.
	 *
	 * @returns {Promise<void>}
	 * An empty promise.
	 * @memberof ActionRequiredWorkItem
	 */
	public completeWorkItem(): Promise<void>
	{
		return this.itemReviewed(
			'Complete');
	}

	/**
	 * Marks the work item as blocked.
	 *
	 * @returns {Promise<void>}
	 * An empty promise.
	 * @memberof ActionRequiredWorkItem
	 */
	public setAsBlocked(): Promise<void>
	{
		return this.itemReviewed(
			'Blocked');
	}

	/**
	 * Sets the work item as unblocked.
	 *
	 * @returns {Promise<void>}
	 * An empty promise.
	 * @memberof ActionRequiredWorkItem
	 */
	public setAsUnblocked(): Promise<void>
	{
		return this.itemReviewed(
			'Unblocked');
	}

		/**
	 * Executes action for current work item review state.
	 *
	 * @async
	 * @param {string} reviewState
	 * The review state to mark
	 * @returns {Promise<void>}
	 * An empty promise.
	 * @memberof ActionRequiredWorkItem
	 */
	public async itemReviewed(
		reviewState: string): Promise<void>
	{
		EventHelper.dispatchBannerEvent(
			'<strong>Updating</strong> Action Required Status and Business Rules',
			`Status set as '${reviewState}'.`,
			AppConstants.activityStatus.info);

		await this.activityService.handleActivity(
			new Activity(
				this.executeWorkItemAction(
				reviewState),
				'<strong>Updating</strong> Action Required Status and Business Rules',
				'<strong>Updated</strong> Action Required Status and Business Rules',
				`Status set to '${reviewState}'.`,
				'Status was not updated.'));
	}
}